import type { IdeaCardBodySection } from './idea-card-types';

const SURROUNDING_MARKDOWN_RE = /^\s*[\*_`~]+|[\*_`~]+\s*$/g;
const LEADING_ORDINAL_RE = /^\s*\d+\s*[\.\)\-:]\s+/;
const ORPHAN_LEADING_EMPHASIS_RE = /^(\s*(?:[-+*]\s+)?)((?:\*\*|__)+)\s+(?=\S)/;
const ORPHAN_TRAILING_EMPHASIS_RE = /(\S)\s+((?:\*\*|__)+)\s*$/;
const SIMPLE_ORPHAN_LEADING_EMPHASIS_RE = /^(\s*)(?:\*\*|__)\s+(?=\S)/;
const SIMPLE_ORPHAN_TRAILING_EMPHASIS_RE = /(\S)\s+(?:\*\*|__)\s*$/;
const STANDALONE_EMPHASIS_RE = /^\s*(?:\*\*|__)+\s*$/;
const SECTION_LABEL_RE = /^(?:\*\*|__)?([^:\n]{1,80}?)(?:\*\*|__)?[:\uFF1A]\s*$/;
const PRESERVED_MARKDOWN_LINK_RE = /(\[[^\]]+\]\([^)]+\)|<https?:\/\/[^>]+>)/g;
const URLISH_TOKEN_RE = /(^|[\s(>])((?:https?:\/\/[^\s<]+|www\.[^\s<]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<]*)?))/gi;
const TRAILING_URL_PUNCTUATION_RE = /[.,!?;:)\]]+$/;

const stripSurroundingMarkdown = (value: string): string =>
  value.replace(SURROUNDING_MARKDOWN_RE, '').trim();

export const cleanIdeaCardTitleText = (value: string, fallback = 'Card'): string => {
  const cleaned = stripSurroundingMarkdown(
    value.replace(/\s+/g, ' ').replace(LEADING_ORDINAL_RE, '')
  )
    .trim();

  return cleaned || fallback;
};

export const sanitizeIdeaCardBodyMarkdown = (value: string): string =>
  value
    .split(/\r?\n/)
    .map((line) => {
      if (STANDALONE_EMPHASIS_RE.test(line)) {
        return '';
      }

      return line
        .replace(ORPHAN_LEADING_EMPHASIS_RE, '$1')
        .replace(SIMPLE_ORPHAN_LEADING_EMPHASIS_RE, '$1')
        .replace(ORPHAN_TRAILING_EMPHASIS_RE, '$1')
        .replace(SIMPLE_ORPHAN_TRAILING_EMPHASIS_RE, '$1');
    })
    .join('\n')
    .trim();

const linkifyMarkdownSegment = (segment: string): string =>
  segment.replace(URLISH_TOKEN_RE, (match, prefix: string, rawUrl: string) => {
    const trailingPunctuationMatch = rawUrl.match(TRAILING_URL_PUNCTUATION_RE);
    const trailingPunctuation = trailingPunctuationMatch?.[0] || '';
    const cleanUrl = trailingPunctuation ? rawUrl.slice(0, -trailingPunctuation.length) : rawUrl;

    if (!cleanUrl || cleanUrl.includes('@')) {
      return match;
    }

    const href = /^https?:\/\//i.test(cleanUrl)
      ? cleanUrl
      : `https://${cleanUrl}`;

    return `${prefix}[${cleanUrl}](${href})${trailingPunctuation}`;
  });

export const linkifyIdeaCardMarkdown = (value: string): string => {
  if (!value) return value;

  return value
    .split(PRESERVED_MARKDOWN_LINK_RE)
    .map((segment, index) => (index % 2 === 1 ? segment : linkifyMarkdownSegment(segment)))
    .join('');
};

const cleanIdeaCardSectionLabel = (value: string): string =>
  stripSurroundingMarkdown(value.replace(/\s+/g, ' ')).trim();

const parseSectionLabel = (line: string): string | null => {
  const trimmed = line.trim();
  const match = trimmed.match(SECTION_LABEL_RE);
  if (!match) return null;

  const label = cleanIdeaCardSectionLabel(match[1] || '');
  return label || null;
};

export const parseSectionedHorizontalBody = (value: string): IdeaCardBodySection[] | undefined => {
  const chunks = value
    .split(/\n\s*\n+/)
    .map((chunk) => sanitizeIdeaCardBodyMarkdown(chunk))
    .filter(Boolean);

  if (chunks.length === 0) {
    return undefined;
  }

  const labeledChunkCount = chunks.filter((chunk) => {
    const [firstLine] = chunk.split(/\r?\n/);
    return Boolean(
      parseSectionLabel(firstLine || '') ||
      firstLine.match(/^(?:\*\*|__)?([^:\n]{1,80}?)(?:\*\*|__)?[:\uFF1A]\s+(.+)$/)
    );
  }).length;

  if (labeledChunkCount === 0) {
    return undefined;
  }

  const sections = chunks
    .map((chunk, index) => {
      const lines = chunk.split(/\r?\n/);
      const firstLine = lines[0] || '';
      const parsedLabel = parseSectionLabel(firstLine);

      if (parsedLabel) {
        const normalizedContent = sanitizeIdeaCardBodyMarkdown(lines.slice(1).join('\n'));
        if (!normalizedContent) {
          return null;
        }

        return {
          label: parsedLabel,
          content: normalizedContent,
        };
      }

      const inlineLabelMatch = firstLine.match(/^(?:\*\*|__)?([^:\n]{1,80}?)(?:\*\*|__)?[:\uFF1A]\s+(.+)$/);
      if (inlineLabelMatch) {
        const inlineLabel = cleanIdeaCardSectionLabel(inlineLabelMatch[1] || '');
        const inlineContent = sanitizeIdeaCardBodyMarkdown(
          [inlineLabelMatch[2], ...lines.slice(1)].join('\n')
        );

        if (!inlineLabel || !inlineContent) {
          return null;
        }

        return {
          label: inlineLabel,
          content: inlineContent,
        };
      }

      if (index === 0 && labeledChunkCount < chunks.length) {
        return {
          content: sanitizeIdeaCardBodyMarkdown(chunk),
        };
      }

      return null;
    })
    .filter((section): section is IdeaCardBodySection => section !== null && Boolean(section.content));

  return sections.length > 0 && labeledChunkCount === sections.filter((section) => section.label).length
    ? sections
    : undefined;
};

export const serializeIdeaCardBodySections = (sections: IdeaCardBodySection[]): string =>
  sections
    .map((section) =>
      section.displayLabel || section.label
        ? `${section.displayLabel || section.label}:\n${section.displayContent || section.content}`
        : section.displayContent || section.content
    )
    .join('\n\n')
    .trim();
